/**
 * The week: a column per day (Mon to Sun). Things without a time sit at the
 * top of the day, then the timed ones in order. Below a laptop width the
 * days stack into a list so nothing scrolls sideways.
 */
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDay, fromYmd } from "../lib";
import type { CalItem } from "../types";
import { CalChip } from "./CalChip";

const TYPE_ORDER: Record<string, number> = { goal: 0, move: 1, event: 2, live_call: 3, appointment: 3, task: 4 };

export function sortDay(items: CalItem[]): { untimed: CalItem[]; timed: CalItem[] } {
  const untimed = items.filter((i) => !i.start).sort((a, b) =>
    (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9) || Number(!!a.ghost) - Number(!!b.ghost) || Number(!!a.done) - Number(!!b.done));
  const timed = items.filter((i) => i.start).sort((a, b) => a.start!.localeCompare(b.start!));
  return { untimed, timed };
}

export function WeekGrid({
  days, items, today, onOpen, onAdd, showOwner,
}: {
  days: string[];
  items: CalItem[];
  today: string;
  onOpen: (it: CalItem) => void;
  onAdd: (day: string) => void;
  showOwner: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 lg:grid-cols-7">
      {days.map((d) => {
        const { untimed, timed } = sortDay(items.filter((i) => i.date === d));
        const isToday = d === today;
        const past = d < today;
        const date = fromYmd(d);
        const empty = !untimed.length && !timed.length;
        return (
          <div
            key={d}
            className={cn(
              "cc-card group flex min-w-0 flex-col p-2 lg:min-h-[460px]",
              isToday && "cc-glow",
              past && !isToday && "opacity-75",
            )}
          >
            <div className="mb-2 flex items-center gap-2 px-1 pt-0.5 lg:flex-col lg:items-start lg:gap-0">
              <div className={cn("text-[11px] font-semibold uppercase tracking-wider", isToday ? "text-indigo-300" : "text-zinc-500")}>
                {date.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-lg font-semibold tabular-nums leading-tight",
                  isToday ? "flex h-7 min-w-7 items-center justify-center rounded-full cc-brand-bg px-1.5 text-white" : "text-zinc-100",
                )}>
                  {date.getDate()}
                </span>
                <span className="text-xs text-zinc-500 lg:hidden">{fmtDay(d, { month: "short" })}</span>
                {isToday && <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300 lg:hidden">Today</span>}
              </div>
              <button
                type="button"
                onClick={() => onAdd(d)}
                className="ml-auto rounded-md p-1 text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-100 lg:hidden"
                aria-label={`Add a task on ${fmtDay(d)}`}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-1 flex-col gap-1.5">
              {untimed.map((it) => <CalChip key={it.id} it={it} onOpen={onOpen} showOwner={showOwner} />)}
              {untimed.length > 0 && timed.length > 0 && <div className="my-0.5 border-t border-dashed border-zinc-800" />}
              {timed.map((it) => <CalChip key={it.id} it={it} onOpen={onOpen} showOwner={showOwner} />)}
              {empty && <div className="px-1 text-[11px] text-zinc-600 lg:pt-1">Nothing planned</div>}
              <button
                type="button"
                onClick={() => onAdd(d)}
                className="mt-auto hidden items-center justify-center gap-1 rounded-lg border border-dashed border-transparent py-1.5 text-[11px] text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-200 group-hover:border-zinc-800 lg:flex"
              >
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
